import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import XKDK_ABI from 'abis/XKDK.json'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import Loader from 'components/Loader'
import Row, { RowBetween } from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import TimeBetweenText from 'components/TimeBetweenText'
import { XKDK_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import useStaking from 'hooks/useStaking'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useCallback, useState } from 'react'
import { Lock, Unlock, X } from 'react-feather'
import { Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { useTheme } from 'styled-components'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'

import CancelRedeemStep from './CancelRedeemStep'
import FinalizeRedeemStep from './FinalizeRedeemStep'
import { CancelVestingButton, WithdrawVestingButton } from './styleds'

const Item = ({
  endTime,
  kdkAmount,
  xKdkAmount,
  index,
}: {
  index: number
  endTime: number
  kdkAmount: CurrencyAmount<Token>
  xKdkAmount: CurrencyAmount<Token>
}) => {
  console.log(xKdkAmount.toExact(), 'DEBUG:X')
  const theme = useTheme()
  const isLocked = endTime > Date.now() / 1000

  const { chainId, account, wagmiPublicClient } = useConnection()
  const xKdkAddress = XKDK_ADDRESSES[chainId]
  const [showModal, setShowModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [txHash, setTxHash] = useState<string>('')
  const [cancelAttemptingTxn, setCancelAttemptingTxn] = useState(false)
  const [cancelTxHash, setCancelTxHash] = useState<string>('')
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const onRedeem = useCallback(async () => {
    if (!xKdkAddress || !wagmiPublicClient || !account) return
    setAttemptingTxn(true)
    try {
      const tx = {
        address: xKdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'finalizeRedeem',
        args: [BigInt(index)],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const hash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setTxHash(hash)
      addTransaction(
        { hash },
        {
          type: TransactionType.STAKE_HARVEST_KDK,
          amount: `${kdkAmount.toSignificant(6)} ${kdkAmount.currency.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash,
      })
    } catch (error) {
      console.error('Error redeeming xKDK:', error)
    } finally {
      setAttemptingTxn(false)
    }
  }, [
    index,
    xKdkAddress,
    wagmiPublicClient,
    account,
    kdkAmount,
    addTransaction,
    writeContractAsync,
    waitForTransactionReceipt,
  ])

  const onCancelRedeem = useCallback(async () => {
    if (!xKdkAddress || !wagmiPublicClient || !account) return
    setCancelAttemptingTxn(true)
    try {
      const tx = {
        address: xKdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'cancelRedeem',
        args: [BigInt(index)],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const hash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setCancelTxHash(hash)
      addTransaction(
        { hash },
        {
          type: TransactionType.STAKE_CANCEL_REDEEM,
          amount: `${xKdkAmount.toSignificant(6)} ${xKdkAmount.currency.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash,
      })
    } catch (error) {
      console.error('Error canceling xKDK redeem:', error)
    } finally {
      setCancelAttemptingTxn(false)
    }
  }, [
    index,
    xKdkAddress,
    wagmiPublicClient,
    account,
    xKdkAmount,
    addTransaction,
    writeContractAsync,
    waitForTransactionReceipt,
  ])

  const step = useStepWithNext(`FinalizeRedeemStep-${index}`, FinalizeRedeemStep, {
    amount: kdkAmount,
    onFinalize: onRedeem,
    pendingText: (
      <>
        Redeem {kdkAmount.toSignificant(6)} {kdkAmount.currency.symbol}
      </>
    ),
    attemptingTxn,
    hash: txHash,
    transactionWaiting: true,
  })

  const handleRedeemOpen = () => {
    setTxHash('')
    setAttemptingTxn(false)
    setShowModal(true)
  }

  const handleRedeemClose = () => {
    setShowModal(false)
    setTxHash('')
    setAttemptingTxn(false)
  }

  const cancelStep = useStepWithNext(`CancelRedeemStep-${index}`, CancelRedeemStep, {
    xKdkAmount,
    onCancel: onCancelRedeem,
    pendingText: (
      <>
        Canceling redeem of {xKdkAmount.toSignificant(6)} {xKdkAmount.currency.symbol}
      </>
    ),
    attemptingTxn: cancelAttemptingTxn,
    hash: cancelTxHash,
    autoConfirm: true,
    transactionWaiting: true,
  })

  const handleCancelOpen = () => {
    setCancelTxHash('')
    setCancelAttemptingTxn(false)
    setShowCancelModal(true)
  }

  const handleCancelClose = () => {
    setShowCancelModal(false)
    setCancelTxHash('')
    setCancelAttemptingTxn(false)
  }

  return (
    <LightCard>
      <StepLineModal
        header={<>Redeem</>}
        isOpen={showModal}
        onDismiss={handleRedeemClose}
        onFinish={handleRedeemClose}
        steps={[step]}
      />
      <StepLineModal
        header={<>Cancel redeem</>}
        isOpen={showCancelModal}
        onDismiss={handleCancelClose}
        onFinish={handleCancelClose}
        steps={[cancelStep]}
      />
      <RowBetween>
        <Column>
          <Text fontSize={14} color={theme.textPrimary}>
            {kdkAmount.toSignificant(6)} {kdkAmount.currency.symbol}
          </Text>
          <Text style={{ textDecoration: 'line-through' }} fontSize={11} color={theme.textSecondary}>
            {xKdkAmount.toSignificant(6)} {xKdkAmount.currency.symbol}
          </Text>
        </Column>
        <Column gap="6px" style={{ alignItems: 'flex-end' }}>
          <Row width="fit-content" gap="6px" align="center">
            {isLocked ? (
              <Lock color={theme.accentFailure} size={14} opacity={0.6} />
            ) : (
              <WithdrawVestingButton onClick={handleRedeemOpen}>
                <Unlock color={theme.accentSuccess} size={14} opacity={0.6} />
              </WithdrawVestingButton>
            )}
            {isLocked && (
              <Text fontSize={12}>
                <TimeBetweenText date={new Date(endTime * 1000)}>
                  <Loader />
                </TimeBetweenText>
              </Text>
            )}
          </Row>
          <CancelVestingButton onClick={handleCancelOpen} title="Cancel redeem">
            <X size={12} />
            <span>Cancel</span>
          </CancelVestingButton>
        </Column>
      </RowBetween>
    </LightCard>
  )
}

const VestingView = () => {
  const { redeems } = useStaking()

  return (
    <AutoColumn gap="sm">
      {redeems?.map((redeem, index) => (redeem ? <Item key={index} {...redeem} /> : null))}
    </AutoColumn>
  )
}

export default VestingView
